import { MaterialTopTabBarProps } from '@react-navigation/material-top-tabs';
import { useEffect, useRef, useState } from 'react';
import {
  Animated,
  LayoutChangeEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';

import Icon from '@components/shared/Icon/Icon';
import { useFontScale } from '@hooks/useFontScale';

import theme, { getScaledSize } from '../../../themes/theme';
import { Box, Text } from '../Restyle';

export type CustomerTabbarProps = {
  spacing?: number;
  initialRouteName?: string;
  contentContainerStyle?: StyleProp<ViewStyle>;
  activeMemberCount?: number;
  invoiceCount?: number;
  disabled?: boolean;
  invoiceDotColor?: keyof typeof theme.colors;
};

type IProps = MaterialTopTabBarProps & CustomerTabbarProps;

const CustomTabBarTeam = ({
  state,
  descriptors,
  navigation,
  spacing = Platform.OS === 'web' ? theme.spacing.m : 10,
  initialRouteName,
  contentContainerStyle,
  activeMemberCount = 1,
  invoiceCount = 0,
  disabled = false,
  invoiceDotColor,
}: IProps): JSX.Element => {
  const scaleX_value = useRef(new Animated.Value(0)).current;
  const translateX_value = useRef(new Animated.Value(0)).current;
  const hoverScaleX_value = useRef(new Animated.Value(0)).current;
  const hoverTranslateX_value = useRef(new Animated.Value(0)).current;
  let isAnimating = false;

  type Position = {
    index: number;
    width: number;
    x: number;
  };

  const [positions, setPositions] = useState<Position[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { fontScale } = useFontScale();

  // get the label widths on mount
  const onLayout = (event: LayoutChangeEvent, index: number, label: string) => {
    const { width, x } = event.nativeEvent.layout;

    const calcX = x + width / 2;

    setPositions((pos) => {
      // Remove existing position for this index if it exists, then add new one
      const filtered = pos.filter((p) => p.index !== index);
      return [...filtered, { index: index, width: width, x: calcX }];
    });

    if (initialRouteName && initialRouteName === label) {
      scaleX_value.setValue(width);
      translateX_value.setValue(calcX);
    }

    // this for fixing bug indicator is not the right place
    if (state.index > 0 && initialRouteName !== label) {
      scaleX_value.setValue(width);
      translateX_value.setValue(calcX);
    }

    if (!initialRouteName && index === 0) {
      scaleX_value.setValue(width);
      translateX_value.setValue(calcX);
    }
  };

  // Reset positions when counts change to force re-measurement
  useEffect(() => {
    setPositions([]);
  }, [activeMemberCount, invoiceCount]);

  useEffect(() => {
    const newPos = positions.findIndex((i) => i.index === state.index);
    if (newPos !== -1 && positions.length > 0) {
      animate(newPos);
    }
  }, [state, positions]);

  // Handle hover indicator position (web only, no animation)
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    if (hoveredIndex !== null && hoveredIndex !== state.index) {
      const hoveredPos = positions.findIndex((i) => i.index === hoveredIndex);
      if (hoveredPos !== -1 && positions.length > 0) {
        const position = positions[hoveredPos];
        hoverTranslateX_value.setValue(getNewPosX(position));
        hoverScaleX_value.setValue(position.width);
      }
    } else {
      // Hide hover indicator when not hovering or hovering over active tab
      hoverTranslateX_value.setValue(0);
      hoverScaleX_value.setValue(0);
    }
  }, [hoveredIndex, state.index, positions]);

  const getNewPosX = (position: Position) => {
    if (
      Platform.OS !== 'web' &&
      spacing !== theme.spacing.m &&
      spacing !== theme.spacing.l &&
      position.index !== 0
    ) {
      return position.x - theme.spacing.s * position.index;
    } else {
      // https://tasktag.atlassian.net/browse/TA-6136
      // this hard code 47 is from Android phone when the app works. because this bug only happened on Android device.
      return position?.x ?? 47;
    }
  };

  const animate = (newPos: number) => {
    isAnimating = true;
    Animated.parallel([
      Animated.timing(translateX_value, {
        toValue: getNewPosX(positions[newPos]),
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleX_value, {
        toValue: positions[newPos].width,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start(() => {
      isAnimating = false;
    });
  };

  const getIcon = (label: string) => {
    switch (label) {
      case 'Overview':
        return 'Home';
      case 'Members':
        return 'Users';
      case 'Invoice':
        return 'File';
    }
  };

  // basic labels as suggested by react navigation
  const labels = state.routes.map((route, index) => {
    const { options } = descriptors[route.key];
    const label =
      (route.params as any)?.tabBarLabel || (options as any).tabBarLabel;
    const isFocused = state.index === index;
    const isHovered = hoveredIndex === index;
    const isActive = isFocused || isHovered;

    const onPress = () => {
      if (isAnimating) {
        return;
      }
      const event = navigation.emit({
        type: 'tabPress',
        target: route.key,
        canPreventDefault: true,
      });

      if (!isFocused && !event.defaultPrevented) {
        // The `merge: true` option makes sure that the params inside the tab screen are preserved

        // @ts-ignore
        navigation.navigate({ name: route.name, merge: true });
      }
    };

    const getCount = () => {
      if (label === 'Members') {
        return activeMemberCount > 99 ? 99 : activeMemberCount;
      } else if (label === 'Invoice') {
        return invoiceCount > 99 ? 99 : invoiceCount;
      }
      return 0;
    };

    let stateColor: keyof typeof theme.colors;
    if (disabled) {
      stateColor = 'grey04';
    } else if (isActive) {
      stateColor = 'greenSecondary';
    } else {
      stateColor = 'textSecondary';
    }

    return (
      <Box
        key={route.key}
        alignItems='center'
        justifyContent='center'
        onLayout={(event) => onLayout(event, index, label)}>
        <TouchableOpacity
          disabled={disabled}
          accessibilityRole='button'
          accessibilityState={isFocused ? { selected: true } : {}}
          onPress={onPress}
          style={{ paddingHorizontal: spacing }}
          {...(Platform.OS === 'web' && {
            onMouseEnter: () => setHoveredIndex(index),
            onMouseLeave: () => setHoveredIndex(null),
          })}>
          <Box
            accessibilityLabel={label}
            paddingVertical='xs'
            flexDirection='row'
            alignItems='center'>
            <Icon
              name={getIcon(label)}
              color={stateColor}
              height={16}
              width={16}
              marginEnd='xxs'
            />
            <Text marginEnd='xxs' variant='labelSmall' color={stateColor}>
              {label}
            </Text>
            {label === 'Invoice' && !!invoiceDotColor && (
              <Box
                width={8}
                height={8}
                mb='xs'
                borderRadius='xxs'
                backgroundColor={invoiceDotColor}
              />
            )}
            {!(label === 'Invoice' && !!invoiceDotColor) &&
              (label === 'Members' || label === 'Invoice') &&
              !disabled &&
              getCount() > 0 && (
                <Box
                  width={(getCount() > 9 ? 21 : 18) * fontScale}
                  height={(getCount() > 9 ? 21 : 18) * fontScale}
                  backgroundColor={isActive ? 'greenSecondary' : 'black'}
                  justifyContent='center'
                  alignItems='center'
                  style={{ borderRadius: 18 * fontScale }}>
                  <Text
                    variant='metadataSecondary'
                    fontWeight='500'
                    color='white'
                    textAlign='center'>
                    {getCount()}
                  </Text>
                </Box>
              )}
          </Box>
        </TouchableOpacity>
      </Box>
    );
  });

  return (
    <Box height={getScaledSize(42)} flex={1}>
      <Animated.View
        key={`tabs-${activeMemberCount}-${invoiceCount}`}
        style={[styles.container, contentContainerStyle]}>
        {labels}
        {/* Active tab indicator */}
        <Animated.View
          style={[
            disabled ? styles.disabledIndicator : styles.indicator,
            {
              transform: [
                { translateX: translateX_value },
                { scaleX: scaleX_value },
              ],
            },
          ]}
        />
        {/* Hover indicator (web only, only when hovering over non-active tab) */}
        {Platform.OS === 'web' &&
          hoveredIndex !== null &&
          hoveredIndex !== state.index && (
            <Animated.View
              style={[
                disabled ? styles.disabledIndicator : styles.indicator,
                {
                  transform: [
                    { translateX: hoverTranslateX_value },
                    { scaleX: hoverScaleX_value },
                  ],
                },
              ]}
            />
          )}
      </Animated.View>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    height: getScaledSize(42),
  },
  indicator: {
    backgroundColor: theme.colors.greenSecondary,
    height: 2,
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    width: 1,
  },
  disabledIndicator: {
    backgroundColor: theme.colors.grey04,
    height: 2,
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    width: 1,
  },
});

export default CustomTabBarTeam;
